// UploadSessionStorage - Persistent storage for upload sessions
// Phase 3A: Upload Resilience & Recovery

interface StoredUploadSession {
  sessionId: string;
  fileName: string;
  totalChunks: number;
  chunkSize: number;
  uploadedChunks: number[]; // Array of completed chunk indices
  totalBytes: number;
  uploadedBytes: number;
  createdAt: number;
  lastActivity: number;
  fileMetadata: {
    type: string;
    size: number;
    lastModified: number;
  };
  uploadOptions: {
    websiteId: string;
    productId: string;
    fileType: "tracks" | "previews";
    productName?: string;
    artist?: string;
  };
  status: "active" | "completed" | "failed" | "cancelled";
}

interface StorageMetrics {
  used: number;
  available: number;
  total: number;
  sessionCount: number;
}

type StorageBackend = "localStorage" | "indexedDB" | "memory";

class UploadSessionStorage {
  private readonly storageKey = "tsfmusic_upload_sessions";
  private readonly dbName = "TSFMusicUploads";
  private readonly dbVersion = 1;
  private readonly storeName = "uploadSessions";
  private readonly maxSessionAge = 24 * 60 * 60 * 1000; // 24 hours
  private readonly maxStorageSizeMB = 5; // 5MB limit for session storage

  private db: IDBDatabase | null = null;
  private preferredBackend: StorageBackend = "localStorage";
  private memoryFallback = new Map<string, StoredUploadSession>();

  constructor() {
    this.initializeStorage();
  }

  private async initializeStorage(): Promise<void> {
    // Try to determine the best storage backend
    this.preferredBackend = await this.detectBestBackend();

    // Initialize IndexedDB if it's the preferred backend
    if (this.preferredBackend === "indexedDB") {
      await this.initializeIndexedDB();
    }

    // Clean up old sessions on initialization
    await this.cleanupExpiredSessions();
  }

  private async detectBestBackend(): Promise<StorageBackend> {
    // Check localStorage availability and quota
    try {
      if (typeof localStorage !== "undefined") {
        const testKey = "_test_storage";
        localStorage.setItem(testKey, "test");
        localStorage.removeItem(testKey);

        // Check available storage
        const usage = await this.estimateLocalStorageUsage();
        if (usage < this.maxStorageSizeMB * 1024 * 1024 * 0.8) {
          // 80% threshold
          return "localStorage";
        }
      }
    } catch (error) {
      console.warn("localStorage not available:", error);
    }

    // Check IndexedDB availability
    try {
      if (typeof indexedDB !== "undefined") {
        // Test if IndexedDB is actually usable (some browsers lie)
        const testReq = indexedDB.open("_test_db", 1);
        return new Promise((resolve) => {
          testReq.onsuccess = () => {
            testReq.result.close();
            indexedDB.deleteDatabase("_test_db");
            resolve("indexedDB");
          };
          testReq.onerror = () => resolve("memory");
          testReq.onblocked = () => resolve("memory");
        });
      }
    } catch (error) {
      console.warn("IndexedDB not available:", error);
    }

    // Fall back to memory storage
    console.warn("Using memory fallback for upload session storage");
    return "memory";
  }

  private async initializeIndexedDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error("Failed to open IndexedDB:", request.error);
        this.preferredBackend = "memory";
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, {
            keyPath: "sessionId",
          });
          store.createIndex("createdAt", "createdAt", { unique: false });
          store.createIndex("status", "status", { unique: false });
        }
      };
    });
  }

  private async estimateLocalStorageUsage(): Promise<number> {
    try {
      let totalSize = 0;
      for (const key in localStorage) {
        if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
          totalSize += localStorage[key].length + key.length;
        }
      }
      return totalSize;
    } catch {
      return 0;
    }
  }

  /**
   * Save upload session to storage
   */
  async saveSession(session: StoredUploadSession): Promise<boolean> {
    try {
      session.lastActivity = Date.now();

      switch (this.preferredBackend) {
        case "localStorage":
          return await this.saveToLocalStorage(session);
        case "indexedDB":
          return await this.saveToIndexedDB(session);
        case "memory":
          return this.saveToMemory(session);
        default:
          return false;
      }
    } catch (error) {
      console.error("Failed to save upload session:", error);

      // Try fallback storage
      if (this.preferredBackend !== "memory") {
        return this.saveToMemory(session);
      }

      return false;
    }
  }

  private async saveToLocalStorage(
    session: StoredUploadSession,
  ): Promise<boolean> {
    try {
      const sessions = (await this.loadAllSessions()) || [];
      const existingIndex = sessions.findIndex(
        (s) => s.sessionId === session.sessionId,
      );

      if (existingIndex >= 0) {
        sessions[existingIndex] = session;
      } else {
        sessions.push(session);
      }

      const serialized = JSON.stringify(sessions);

      // Check size before storing
      if (serialized.length > this.maxStorageSizeMB * 1024 * 1024) {
        console.warn(
          "Session storage would exceed size limit, cleaning up old sessions",
        );
        await this.cleanupExpiredSessions();

        // Try again with cleaned up data
        const cleanedSessions = (await this.loadAllSessions()) || [];
        const updatedIndex = cleanedSessions.findIndex(
          (s) => s.sessionId === session.sessionId,
        );
        if (updatedIndex >= 0) {
          cleanedSessions[updatedIndex] = session;
        } else {
          cleanedSessions.push(session);
        }

        localStorage.setItem(this.storageKey, JSON.stringify(cleanedSessions));
      } else {
        localStorage.setItem(this.storageKey, serialized);
      }

      return true;
    } catch (error) {
      if (error.name === "QuotaExceededError") {
        console.warn(
          "LocalStorage quota exceeded, switching to memory storage",
        );
        this.preferredBackend = "memory";
        return this.saveToMemory(session);
      }
      throw error;
    }
  }

  private async saveToIndexedDB(
    session: StoredUploadSession,
  ): Promise<boolean> {
    if (!this.db) return false;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.put(session);

      request.onsuccess = () => resolve(true);
      request.onerror = () => {
        console.error("IndexedDB save failed:", request.error);
        resolve(false);
      };
    });
  }

  private saveToMemory(session: StoredUploadSession): boolean {
    this.memoryFallback.set(session.sessionId, session);
    return true;
  }

  /**
   * Load a specific upload session
   */
  async loadSession(sessionId: string): Promise<StoredUploadSession | null> {
    try {
      switch (this.preferredBackend) {
        case "localStorage":
          return await this.loadFromLocalStorage(sessionId);
        case "indexedDB":
          return await this.loadFromIndexedDB(sessionId);
        case "memory":
          return this.loadFromMemory(sessionId);
        default:
          return null;
      }
    } catch (error) {
      console.error("Failed to load upload session:", error);
      return null;
    }
  }

  private async loadFromLocalStorage(
    sessionId: string,
  ): Promise<StoredUploadSession | null> {
    try {
      const sessions = await this.loadAllSessions();
      return sessions?.find((s) => s.sessionId === sessionId) || null;
    } catch (error) {
      console.error("LocalStorage load failed:", error);
      return null;
    }
  }

  private async loadFromIndexedDB(
    sessionId: string,
  ): Promise<StoredUploadSession | null> {
    if (!this.db) return null;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.get(sessionId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => {
        console.error("IndexedDB load failed:", request.error);
        resolve(null);
      };
    });
  }

  private loadFromMemory(sessionId: string): StoredUploadSession | null {
    return this.memoryFallback.get(sessionId) || null;
  }

  /**
   * Load all upload sessions
   */
  async loadAllSessions(): Promise<StoredUploadSession[] | null> {
    try {
      switch (this.preferredBackend) {
        case "localStorage":
          return this.loadAllFromLocalStorage();
        case "indexedDB":
          return await this.loadAllFromIndexedDB();
        case "memory":
          return Array.from(this.memoryFallback.values());
        default:
          return null;
      }
    } catch (error) {
      console.error("Failed to load upload sessions:", error);
      return null;
    }
  }

  private loadAllFromLocalStorage(): StoredUploadSession[] | null {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Failed to parse stored sessions:", error);
      return null;
    }
  }

  private async loadAllFromIndexedDB(): Promise<StoredUploadSession[]> {
    if (!this.db) return [];

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => {
        console.error("IndexedDB loadAll failed:", request.error);
        resolve([]);
      };
    });
  }

  /**
   * Delete a specific upload session
   */
  async deleteSession(sessionId: string): Promise<boolean> {
    try {
      switch (this.preferredBackend) {
        case "localStorage":
          return await this.deleteFromLocalStorage(sessionId);
        case "indexedDB":
          return await this.deleteFromIndexedDB(sessionId);
        case "memory":
          return this.deleteFromMemory(sessionId);
        default:
          return false;
      }
    } catch (error) {
      console.error("Failed to delete upload session:", error);
      return false;
    }
  }

  private async deleteFromLocalStorage(sessionId: string): Promise<boolean> {
    try {
      const sessions = await this.loadAllSessions();
      if (!sessions) return false;

      const filtered = sessions.filter((s) => s.sessionId !== sessionId);
      localStorage.setItem(this.storageKey, JSON.stringify(filtered));
      return true;
    } catch (error) {
      console.error("LocalStorage delete failed:", error);
      return false;
    }
  }

  private async deleteFromIndexedDB(sessionId: string): Promise<boolean> {
    if (!this.db) return false;

    return new Promise((resolve) => {
      const transaction = this.db!.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(sessionId);

      request.onsuccess = () => resolve(true);
      request.onerror = () => {
        console.error("IndexedDB delete failed:", request.error);
        resolve(false);
      };
    });
  }

  private deleteFromMemory(sessionId: string): boolean {
    return this.memoryFallback.delete(sessionId);
  }

  /**
   * Clean up expired sessions (older than 24 hours)
   */
  async cleanupExpiredSessions(): Promise<number> {
    const cutoffTime = Date.now() - this.maxSessionAge;
    let cleanedCount = 0;

    try {
      const sessions = await this.loadAllSessions();
      if (!sessions) return 0;

      const validSessions = sessions.filter((session) => {
        const isExpired = session.lastActivity < cutoffTime;
        if (isExpired) cleanedCount++;
        return !isExpired;
      });

      if (cleanedCount > 0) {
        switch (this.preferredBackend) {
          case "localStorage":
            localStorage.setItem(
              this.storageKey,
              JSON.stringify(validSessions),
            );
            break;
          case "indexedDB":
            // Clear and rebuild IndexedDB
            if (this.db) {
              const transaction = this.db.transaction(
                [this.storeName],
                "readwrite",
              );
              const store = transaction.objectStore(this.storeName);
              await new Promise((resolve) => {
                store.clear().onsuccess = () => resolve(true);
              });

              for (const session of validSessions) {
                store.put(session);
              }
            }
            break;
          case "memory":
            this.memoryFallback.clear();
            validSessions.forEach((session) => {
              this.memoryFallback.set(session.sessionId, session);
            });
            break;
        }
      }
    } catch (error) {
      console.error("Session cleanup failed:", error);
    }

    return cleanedCount;
  }

  /**
   * Get recoverable sessions (active or failed sessions less than 24 hours old)
   */
  async getRecoverableSessions(): Promise<StoredUploadSession[]> {
    const sessions = await this.loadAllSessions();
    if (!sessions) return [];

    const cutoffTime = Date.now() - this.maxSessionAge;

    return sessions
      .filter(
        (session) =>
          session.lastActivity >= cutoffTime &&
          (session.status === "active" || session.status === "failed") &&
          session.uploadedChunks.length > 0, // Only sessions with some progress
      )
      .sort((a, b) => b.lastActivity - a.lastActivity); // Most recent first
  }

  /**
   * Get storage usage metrics
   */
  async getStorageMetrics(): Promise<StorageMetrics> {
    const sessions = (await this.loadAllSessions()) || [];
    let used = 0;

    try {
      switch (this.preferredBackend) {
        case "localStorage":
          used = await this.estimateLocalStorageUsage();
          break;
        case "indexedDB":
          // Estimate IndexedDB usage (approximation)
          used = JSON.stringify(sessions).length * 1.2; // Add overhead estimate
          break;
        case "memory":
          used = JSON.stringify(
            Array.from(this.memoryFallback.values()),
          ).length;
          break;
      }
    } catch (error) {
      console.error("Failed to calculate storage usage:", error);
    }

    return {
      used,
      available: this.maxStorageSizeMB * 1024 * 1024 - used,
      total: this.maxStorageSizeMB * 1024 * 1024,
      sessionCount: sessions.length,
    };
  }

  /**
   * Clear all stored sessions (useful for testing or manual cleanup)
   */
  async clearAllSessions(): Promise<boolean> {
    try {
      switch (this.preferredBackend) {
        case "localStorage":
          localStorage.removeItem(this.storageKey);
          break;
        case "indexedDB":
          if (this.db) {
            const transaction = this.db.transaction(
              [this.storeName],
              "readwrite",
            );
            const store = transaction.objectStore(this.storeName);
            await new Promise((resolve) => {
              store.clear().onsuccess = () => resolve(true);
            });
          }
          break;
        case "memory":
          this.memoryFallback.clear();
          break;
      }
      return true;
    } catch (error) {
      console.error("Failed to clear sessions:", error);
      return false;
    }
  }

  /**
   * Get current storage backend being used
   */
  getStorageBackend(): StorageBackend {
    return this.preferredBackend;
  }

  /**
   * Check if storage is available and functioning
   */
  async isStorageHealthy(): Promise<boolean> {
    try {
      const testSession: StoredUploadSession = {
        sessionId: "_health_check",
        fileName: "test",
        totalChunks: 1,
        chunkSize: 1024,
        uploadedChunks: [],
        totalBytes: 1024,
        uploadedBytes: 0,
        createdAt: Date.now(),
        lastActivity: Date.now(),
        fileMetadata: { type: "test", size: 1024, lastModified: Date.now() },
        uploadOptions: {
          websiteId: "test",
          productId: "test",
          fileType: "tracks",
        },
        status: "active",
      };

      const saved = await this.saveSession(testSession);
      if (!saved) return false;

      const loaded = await this.loadSession("_health_check");
      if (!loaded) return false;

      await this.deleteSession("_health_check");
      return true;
    } catch (error) {
      console.error("Storage health check failed:", error);
      return false;
    }
  }
}

// Export singleton instance
export const uploadSessionStorage = new UploadSessionStorage();

export type { StoredUploadSession, StorageMetrics, StorageBackend };
